import React from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import InputFilterItem from '../../../../components/InputFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';

export interface AbAddressReviewFilterOptions {
  hubId?: string;
  id?: string;
  aAddress?: string;
  bAddress?: string;
  reporterName?: string;
  status?: string;
}

export const DEFAULT_AB_ADDRESS_REVIEW_FILTER_OPTIONS: AbAddressReviewFilterOptions = {
  hubId: undefined,
  id: undefined,
  aAddress: undefined,
  bAddress: undefined,
  reporterName: undefined,
  status: undefined,
};

interface HubOption {
  label: string;
  value: number;
}

interface AbAddressReviewFilterAreaProps {
  filters: AbAddressReviewFilterOptions;
  setFilters: (filters: AbAddressReviewFilterOptions) => void;
  hubOptions: HubOption[];
}

const digitsOnly = (value: string): string => value.replace(/[^0-9]/g, '');

const AbAddressReviewFilterArea: React.FC<AbAddressReviewFilterAreaProps> = ({
  filters,
  setFilters,
  hubOptions,
}) => {
  const { t } = useTranslation();

  const handleChange = (key: keyof AbAddressReviewFilterOptions, value: string | undefined) => {
    const processed = typeof value === 'string' && value.trim() === '' ? undefined : value;
    setFilters({ ...filters, [key]: processed });
  };

  const colLabels = [
    t('mapTool.abAddressReview.filters.hub'),
    t('mapTool.abAddressReview.filters.id'),
    t('mapTool.abAddressReview.filters.aAddress'),
    t('mapTool.abAddressReview.filters.bAddress'),
    t('mapTool.abAddressReview.filters.reporter'),
    t('mapTool.abAddressReview.filters.status'),
  ];
  const colSpanClassName = getSmartLabelClassName(colLabels);
  const statusContainerClassName = useFilterContainerClassName();
  const statusLabelClassName = useFilterLabelClassName(colSpanClassName);
  const statusInputContainerClassName = useFilterInputContainerClassName();

  const statusOptions = [
    { label: t('mapTool.abAddressReview.status.pending'), value: '0' },
    { label: t('mapTool.abAddressReview.status.approved'), value: '1' },
    { label: t('mapTool.abAddressReview.status.rejected'), value: '2' },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      <div className="mb-2 py-2">
        <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
          <div className="min-w-0">
            <div className={statusContainerClassName}>
              <span className={statusLabelClassName}>
                {t('mapTool.abAddressReview.filters.hub')}
              </span>
              <div className={statusInputContainerClassName}>
                <Select
                  className="w-full"
                  size="middle"
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  placeholder={t('mapTool.abAddressReview.placeholders.hub') as string}
                  value={filters.hubId ? Number(filters.hubId) : undefined}
                  options={hubOptions}
                  onChange={(value?: number) =>
                    handleChange('hubId', value != null ? String(value) : undefined)
                  }
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <InputFilterItem
              label={t('mapTool.abAddressReview.filters.id')}
              value={filters.id ?? ''}
              spanClassName={colSpanClassName}
              onChange={(e) => handleChange('id', digitsOnly(e.target.value))}
              placeholder={t('mapTool.abAddressReview.placeholders.id') as string}
            />
          </div>
          <div className="min-w-0">
            <InputFilterItem
              label={t('mapTool.abAddressReview.filters.aAddress')}
              value={filters.aAddress ?? ''}
              spanClassName={colSpanClassName}
              onChange={(e) => handleChange('aAddress', e.target.value)}
              placeholder={t('mapTool.abAddressReview.placeholders.aAddressFuzzy') as string}
            />
          </div>
          <div className="min-w-0">
            <InputFilterItem
              label={t('mapTool.abAddressReview.filters.bAddress')}
              value={filters.bAddress ?? ''}
              spanClassName={colSpanClassName}
              onChange={(e) => handleChange('bAddress', e.target.value)}
              placeholder={t('mapTool.abAddressReview.placeholders.bAddressFuzzy') as string}
            />
          </div>
          <div className="min-w-0">
            <InputFilterItem
              label={t('mapTool.abAddressReview.filters.reporter')}
              value={filters.reporterName ?? ''}
              spanClassName={colSpanClassName}
              onChange={(e) => handleChange('reporterName', e.target.value)}
              placeholder={t('mapTool.abAddressReview.placeholders.reporter') as string}
            />
          </div>
          <div className="min-w-0">
            <div className={statusContainerClassName}>
              <span className={statusLabelClassName}>
                {t('mapTool.abAddressReview.filters.status')}
              </span>
              <div className={statusInputContainerClassName}>
                <Select
                  className="w-full"
                  size="middle"
                  allowClear
                  placeholder={t('components.input.select') as string}
                  value={filters.status}
                  options={statusOptions}
                  onChange={(value) => handleChange('status', value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbAddressReviewFilterArea;
